/**
 * QuickAssignPopover Component
 *
 * A popover dropdown that appears when clicking an unassigned cell in the Daily Work Queue.
 * Three variants: station, operator, packer.
 * Uses existing stationManagementService APIs to perform assignments.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import stationManagementService, { StationWithDetails } from '../services/stationManagementService';
import { ScheduledOperator, ScheduledPacker } from '../services/workScheduleService';
import { useLanguage } from '../i18n/LanguageContext';

export type AssignType = 'station' | 'operator' | 'packer';

interface QuickAssignPopoverProps {
  type: AssignType;
  subSessionNumber: string;
  anchorEl: HTMLElement;
  operators: ScheduledOperator[];
  packers: ScheduledPacker[];
  onClose: () => void;
  onAssigned: () => void;
  /** When set, the popover is in "reassign" mode — shows all options, not just unassigned */
  currentAssigneeId?: string;
  /** Sub-session status — used to decide whether a reason is required for station reassignment */
  subSessionStatus?: string;
}

/** Reason keys matching i18n sessionDetails.stationChangeReasons */
const STATION_CHANGE_REASONS = [
  'equipment_issue',
  'workload_balance',
  'operator_request',
  'supervisor_decision',
  'station_maintenance',
  'other',
] as const;

const QuickAssignPopover: React.FC<QuickAssignPopoverProps> = ({
  type,
  subSessionNumber,
  anchorEl,
  operators,
  packers,
  onClose,
  onAssigned,
  currentAssigneeId,
  subSessionStatus,
}) => {
  const { t } = useLanguage();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [stations, setStations] = useState<StationWithDetails[]>([]);
  const [loadingStations, setLoadingStations] = useState(false);

  // Reason step state — for station reassignment on active/paused sub-sessions
  const [pendingStationId, setPendingStationId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [reasonOther, setReasonOther] = useState('');
  const needsReason = type === 'station' && ['active', 'paused'].includes(subSessionStatus || '');

  // Fetch stations only for station variant
  useEffect(() => {
    if (type === 'station') {
      setLoadingStations(true);
      stationManagementService.getStations()
        .then(setStations)
        .catch(() => toast.error('Failed to load stations'))
        .finally(() => setLoadingStations(false));
    }
  }, [type]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorEl, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Position the popover below the anchor element
  const [position, setPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    const rect = anchorEl.getBoundingClientRect();
    setPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    });
  }, [anchorEl]);

  // Build option list based on type
  const options = useMemo(() => {
    const q = search.toLowerCase();
    if (type === 'station') {
      return stations
        .filter(s => s.station_id != null && s.enabled)
        .filter(s => !q || s.station_name.toLowerCase().includes(q))
        .map(s => {
          const isCurrent = String(s.station_id) === currentAssigneeId;
          return {
            id: String(s.station_id),
            label: s.station_name,
            sublabel: isCurrent
              ? t('dailyWorkQueue.currentAssignee')
              : s.session_status ? (t(`status.${s.session_status}`) || s.session_status) : undefined,
            busy: isCurrent || (!!s.session_status && ['active', 'paused'].includes(s.session_status)),
          };
        });
    }
    if (type === 'operator') {
      const isReassign = !!currentAssigneeId;
      return operators
        .filter(op => isReassign ? true : !op.is_assigned)
        .filter(op => !q || (op.full_name || op.employee_number).toLowerCase().includes(q))
        .map(op => {
          const isCurrent = op.employee_number === currentAssigneeId;
          return {
            id: op.employee_number,
            label: op.full_name || op.employee_number,
            sublabel: isCurrent
              ? t('dailyWorkQueue.currentAssignee')
              : op.is_assigned
                ? op.station_name || t('dailyWorkQueue.assigned')
                : undefined,
            busy: isCurrent,
          };
        })
        .sort((a, b) => {
          // Current assignee first, then unassigned, then assigned elsewhere
          if (a.busy !== b.busy) return a.busy ? -1 : 1;
          if (a.sublabel && !b.sublabel) return 1;
          if (!a.sublabel && b.sublabel) return -1;
          return 0;
        });
    }
    // packer
    const isReassign = !!currentAssigneeId;
    return packers
      .filter(p => isReassign ? true : !p.is_assigned)
      .filter(p => !q || (p.full_name || p.employee_number).toLowerCase().includes(q))
      .map(p => {
        const isCurrent = p.employee_number === currentAssigneeId;
        return {
          id: p.employee_number,
          label: p.full_name || p.employee_number,
          sublabel: isCurrent
            ? t('dailyWorkQueue.currentAssignee')
            : p.is_assigned
              ? t('dailyWorkQueue.assigned')
              : undefined,
          busy: isCurrent,
        };
      })
      .sort((a, b) => {
        if (a.busy !== b.busy) return a.busy ? -1 : 1;
        if (a.sublabel && !b.sublabel) return 1;
        if (!a.sublabel && b.sublabel) return -1;
        return 0;
      });
  }, [type, stations, operators, packers, search, currentAssigneeId, t]);

  const handleAssign = async (optionId: string) => {
    // For station reassignment on active/paused: show reason step first
    if (needsReason && !pendingStationId) {
      setPendingStationId(optionId);
      return;
    }

    setAssigning(true);
    try {
      if (type === 'station') {
        const stationIdToAssign = pendingStationId || optionId;
        const opts = pendingStationId
          ? { reason, reason_other: reason === 'other' ? reasonOther : undefined, confirmed: true }
          : undefined;
        await stationManagementService.assignSubSessionStation(subSessionNumber, Number(stationIdToAssign), opts);
      } else if (type === 'operator') {
        await stationManagementService.assignSubSessionOperator(subSessionNumber, optionId);
      } else {
        await stationManagementService.assignSubSessionPacker(subSessionNumber, optionId);
      }
      toast.success(t('dailyWorkQueue.assignedSuccess'));
      onAssigned();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Assignment failed';
      toast.error(msg);
    } finally {
      setAssigning(false);
    }
  };

  /** Confirm reason and submit station change */
  const handleReasonConfirm = () => {
    if (!reason) return;
    handleAssign(pendingStationId!);
  };

  /** Go back from reason step to station list */
  const handleReasonCancel = () => {
    setPendingStationId(null);
    setReason('');
    setReasonOther('');
  };

  const titleKey = currentAssigneeId
    ? (type === 'station'
      ? 'dailyWorkQueue.reassignStation'
      : type === 'operator'
        ? 'dailyWorkQueue.reassignOperator'
        : 'dailyWorkQueue.reassignPacker')
    : (type === 'station'
      ? 'dailyWorkQueue.assignStation'
      : type === 'operator'
        ? 'dailyWorkQueue.assignOperator'
        : 'dailyWorkQueue.assignPacker');

  const placeholderKey = type === 'station'
    ? 'dailyWorkQueue.selectStation'
    : type === 'operator'
      ? 'dailyWorkQueue.selectOperator'
      : 'dailyWorkQueue.selectPacker';

  const isLoading = type === 'station' && loadingStations;

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl"
      style={{ top: position.top, left: position.left }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          {t(titleKey)}
        </span>
        <button
          onClick={onClose}
          className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Reason step — shown for station reassignment on active/paused sub-sessions */}
      {pendingStationId ? (
        <div className="px-3 py-3 space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('sessionDetails.stationChangeReason')}
          </p>
          <select
            value={reason}
            onChange={e => setReason(e.target.value)}
            className="w-full text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">{t('sessionDetails.selectStationChangeReason')}</option>
            {STATION_CHANGE_REASONS.map(key => (
              <option key={key} value={key}>
                {t(`sessionDetails.stationChangeReasons.${key}`)}
              </option>
            ))}
          </select>
          {reason === 'other' && (
            <input
              type="text"
              value={reasonOther}
              onChange={e => setReasonOther(e.target.value)}
              placeholder={t('sessionDetails.stationChangeOtherPlaceholder')}
              className="w-full text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleReasonCancel}
              className="flex-1 text-sm py-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handleReasonConfirm}
              disabled={!reason || (reason === 'other' && !reasonOther.trim()) || assigning}
              className="flex-1 text-sm py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('sessionDetails.confirmStationChange')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Search (show when more than 5 options) */}
          {options.length > 5 && (
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={t(placeholderKey)}
                  className="w-full pl-7 pr-2 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto py-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                {t('dailyWorkQueue.noAvailable')}
              </div>
            ) : (
              options.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => handleAssign(opt.id)}
                  disabled={assigning || opt.busy}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    opt.busy
                      ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer'
                  } ${assigning ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                        {opt.sublabel}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* Loading overlay when assigning */}
      {assigning && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-800/60 rounded-xl">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      )}
    </div>
  );
};

export default QuickAssignPopover;
